/**
 * Edit Novel Page
 * Form for editing an existing novel
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { NovelForm, type NovelFormData } from "../components/NovelForm";
import { api, ApiError } from "../lib/api";
import type { Novel } from "@novelreader/types";

export default function EditNovel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [novel, setNovel] = useState<Novel | null>(null);
  const [isLoadingNovel, setIsLoadingNovel] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchNovel = useCallback(async () => {
    if (!id) return;

    setIsLoadingNovel(true);
    setLoadError(null);

    try {
      const response = await api.novels.get(id);
      setNovel(response.novel);
    } catch (err) {
      if (err instanceof ApiError) {
        setLoadError(err.message);
      } else {
        setLoadError("Failed to load novel");
      }
    } finally {
      setIsLoadingNovel(false);
    }
  }, [id]);

  useEffect(() => {
    fetchNovel();
  }, [fetchNovel]);

  async function handleSubmit(data: NovelFormData) {
    if (!id) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await api.novels.update(id, {
        title: data.title,
        description: data.description || null,
        status: data.status,
      });

      navigate(`/novels/${id}/chapters`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errors && err.errors.length > 0) {
          setError(err.errors.map((e) => e.message).join(", "));
        } else {
          setError(err.message);
        }
      } else {
        setError("Failed to update novel");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Loading state
  if (isLoadingNovel) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Edit Novel</h1>
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Edit Novel</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 mb-4">{loadError}</p>
          <button
            onClick={fetchNovel}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Novel not found (shouldn't happen if loadError is set, but just in case)
  if (!novel) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Edit Novel</h1>
        <div className="text-slate-600">Novel not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/novels" className="hover:text-slate-700">
          Novels
        </Link>
        <span>/</span>
        <Link to={`/novels/${id}/chapters`} className="hover:text-slate-700">
          {novel.title}
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">Edit</span>
      </nav>

      <h1 className="text-2xl font-bold text-slate-900">Edit Novel</h1>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="max-w-2xl bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <NovelForm
          initialData={novel}
          onSubmit={handleSubmit}
          onCancel={() => navigate(`/novels/${id}/chapters`)}
          isLoading={isSubmitting}
        />
      </div>
    </div>
  );
}
